import { QueryStrings } from '@/config/references'
import { useGetCosts } from '@/services/adapters/useGetCosts'
import { useGetEffects } from '@/services/adapters/useGetEffects'
import { useGetRemedies } from '@/services/adapters/useGetRemedies'
import { useGetRemedy } from '@/services/adapters/useGetRemedy'
import { useGetRemedyCategories } from '@/services/adapters/useGetRemedyCategories'
import { useGetMappedRemediesCurrentRoute } from '@/services/useGetMappedRemedies'
import { getRemedyWithAssociations } from '@/utils/remedy'
import { IBaseMappedRemedy, IRemedy, IRemedyReference } from '@lifk/types'
import { computed, ComputedRef, inject, InjectionKey, provide } from 'vue'
import { useRoute } from 'vue-router'

type TFullRemediesStore = {
  fullRemedies: ComputedRef<IRemedy[]>
  currentRemedies: ComputedRef<IRemedy[]>
  remedyReference: ComputedRef<IRemedyReference | undefined>
}
export const FullRemediesKey = Symbol() as InjectionKey<TFullRemediesStore>

export const provideFullRemedies = (): {
  currentRemedies: ComputedRef<IRemedy[]>
} => {
  const route = useRoute()
  const { data: remedies } = useGetRemedies(true)
  const { data: remedy } = useGetRemedy()
  const { data: remedyCategories } = useGetRemedyCategories()
  const { data: costs } = useGetCosts(true)
  const { data: effects } = useGetEffects(true)
  const { data: mappedRemedies } = useGetMappedRemediesCurrentRoute()

  const fullRemedies: ComputedRef<IRemedy[]> = computed(() =>
    (remedies.value ?? [])
      .map((remedy) =>
        getRemedyWithAssociations(
          remedy,
          remedyCategories.value ?? [],
          effects.value ?? [],
          costs.value ?? []
        )
      )
      .filter((remedy): remedy is IRemedy => !!remedy)
  )

  const currentRemedies: ComputedRef<IRemedy[]> = computed(() =>
    fullRemedies.value.filter((rem) => !rem.deprecated)
  )

  const mappedRemedyFromUrl: ComputedRef<IBaseMappedRemedy | undefined> =
    computed(() => {
      if (
        !mappedRemedies.value?.all ||
        !route.query ||
        !route.query[QueryStrings.mappedRemedy]
      ) {
        return
      }

      const mappedRemedyId = route.query[QueryStrings.mappedRemedy]
      return mappedRemedies.value.all.find((mr) => mr._id === mappedRemedyId)
    })

  const mergedRemedyArticle: ComputedRef<IRemedyReference | undefined> =
    computed(() => {
      const remedyReference = remedy.value
        ? (getRemedyWithAssociations(
            remedy.value,
            remedyCategories.value ?? [],
            effects.value ?? [],
            costs.value ?? []
          ) as IRemedyReference)
        : undefined

      if (mappedRemedyFromUrl.value && !remedyReference) {
        // Displays very old remedies that do not exist in Sanity,
        //   but do have old remedy data saved in the db
        const deprecatedRemedy = getRemedyWithAssociations(
          undefined,
          remedyCategories.value ?? [],
          effects.value ?? [],
          costs.value ?? [],
          mappedRemedyFromUrl.value.remedy
        )
        if (deprecatedRemedy) {
          return {
            ...(deprecatedRemedy as IRemedyReference),
            tips: [],
            resources: [],
            pairings: []
          }
        }
      } else if (remedyReference) {
        return remedyReference
      }
      return undefined
    })

  provide(FullRemediesKey, {
    fullRemedies,
    currentRemedies,
    remedyReference: mergedRemedyArticle
  })

  // Return for use in useFullEffects sibling store
  return { currentRemedies }
}

// Filter out deprecated remedies depending on the use-case
export default function useFullRemedies(
  includeDeprecated = false
): Omit<TFullRemediesStore, 'currentRemedies'> {
  const { fullRemedies, currentRemedies, remedyReference } =
    inject(FullRemediesKey)!

  return {
    fullRemedies: includeDeprecated ? fullRemedies : currentRemedies,
    remedyReference
  }
}
